import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { ArrowUpRight, FileText, Search } from 'lucide-react';
import { dtpApi } from '../../lib/dtpApi';
import { dtpPath } from '../../lib/clinicSite';
import { LabObservationRow, LabStatusBadge } from '../labs/LabResultTable';
import type { LabResultDetail, LabResultSummary } from '../../types/labResults';
import { BODY_REGIONS, observationRegion, type BodyFilter } from './patientBodyRegions';
import { LabFlagScore } from './ReportLabScore';
import { PatientBodyIllustration } from './PatientBodyIllustration';
import styles from './PatientHealthOverview.module.css';
import { track } from '../../lib/analytics';

const interacted = (action: 'report_changed' | 'region_selected' | 'flagged_only' | 'report_opened') => track('health_overview_interacted', { action });

interface Props {
  physicianId?: string;
  patientId?: string;
  reports: LabResultSummary[];
  loading: boolean;
  error: boolean;
  onRetry: () => unknown;
}
const PAGE_SIZE = 20;

export function PatientHealthOverview({ physicianId, patientId, reports, loading, error, onRetry }: Props) {
  const [region, setRegion] = useState<BodyFilter>('all');
  const [selection, setSelection] = useState('');
  const selected = reports.find(report => report.id === selection) || reports[0];
  // Fetch only the report being viewed. The summary list never fans out into
  // a detail request for every report in a patient's history.
  const report = useQuery({
    queryKey: ['patient-home', physicianId, patientId, 'report', selected?.id],
    enabled: !!selected && !error && !loading,
    retry: false,
    gcTime: 0,
    queryFn: async ({ signal }) => {
      const res = await dtpApi.getLabResult(selected!.id, signal);
      if (!res.success || !res.data) throw new Error('Report unavailable');
      return res.data;
    },
  });

  return (
    <section className={styles.overview} aria-labelledby="health-overview-title">
      <header className={styles.header}>
        <div><h2 id="health-overview-title">Your health overview</h2><p>Your lab reports, with the details that matter.</p></div>
        <Link to={dtpPath(physicianId, '/results')}>All lab results <ArrowUpRight size={16} aria-hidden="true" /></Link>
      </header>
      {loading ? <p className={styles.message} role="status">Loading results…</p>
        : error ? <div className={styles.message} role="alert"><p>We couldn’t load your results.</p><button onClick={onRetry}>Try again</button></div>
        : !selected ? <div className={styles.empty}><FileText size={30} aria-hidden="true" /><h3>Your health story starts here</h3><p>When your clinic shares lab results, you’ll find them here.</p></div>
        : <div className={styles.layout}>
          <aside className={styles.sidebar}>
            <label htmlFor="health-report">Explore a report</label>
            <select id="health-report" value={selected.id} onChange={event => { setSelection(event.target.value); setRegion('all'); interacted('report_changed'); }}>
              {reports.map(item => <option key={item.id} value={item.id}>{item.panelName} — {formatReportDate(item.reportedAt || item.receivedAt)}</option>)}
            </select>
            <div className={styles.reportIdentity}>
              <LabStatusBadge status={selected.status} amended={selected.amended} />
              <h3>{selected.panelName}</h3>
              <p>Reported {formatReportDate(selected.reportedAt || selected.receivedAt)}</p>
              {selected.collectedAt && <p>Collected {formatReportDate(selected.collectedAt)}</p>}
            </div>
            {report.data && !report.isError && <div className={styles.bodyIllustration}>
              <PatientBodyIllustration observations={report.data.observations} selected={region} onSelect={next => { setRegion(next); interacted('region_selected'); }} />
            </div>}
            <details className={styles.context}>
              <summary>Reading your results</summary>
              <p>Flags and reference ranges come from the lab. A flag alone does not explain what a result means for you.</p>
              <p>Your clinician can explain these results alongside your history and treatment.</p>
            </details>
            <Link className={styles.reportLink} to={dtpPath(physicianId, `/results/${selected.id}`)} onClick={() => interacted('report_opened')}>Open {selected.panelName} <ArrowUpRight size={16} aria-hidden="true" /></Link>
            <p className={styles.footnote}>View available PDFs and report history in the full report.</p>
          </aside>
          <div className={styles.content} aria-live="polite">
            {report.isPending ? <p className={styles.message} role="status">Loading report details…</p>
              : report.isError ? <div className={styles.message} role="alert"><p>We couldn’t load this report.</p><button onClick={() => report.refetch()}>Try again</button></div>
              : report.data && <ReportExplorer key={`${report.data.id}-${region}`} report={report.data} region={region} onClear={() => setRegion('all')} />}
          </div>
        </div>}
    </section>
  );
}

function ReportExplorer({ report, region, onClear }: { report: LabResultDetail; region: BodyFilter; onClear: () => void }) {
  const [search, setSearch] = useState('');
  const [flaggedOnly, setFlaggedOnly] = useState(false);
  const [page, setPage] = useState(0);
  const observations = report.observations;
  const counts = useMemo(() => ({
    total: observations.length,
    flagged: observations.filter(item => item.isAbnormal).length,
    normal: observations.filter(item => item.flag === 'normal').length,
    unflagged: observations.filter(item => item.flag === 'unflagged').length,
  }), [observations]);
  const filtered = useMemo(() => {
    const needle = search.trim().toLocaleLowerCase();
    return observations.filter(item => (region === 'all' || observationRegion(item) === region) && (!flaggedOnly || item.isAbnormal) && (!needle || `${item.name} ${item.code}`.toLocaleLowerCase().includes(needle)));
  }, [observations, search, flaggedOnly, region]);
  const pages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const currentPage = Math.min(page, pages - 1);
  const visible = filtered.slice(currentPage * PAGE_SIZE, (currentPage + 1) * PAGE_SIZE);

  return <>
    <LabFlagScore observations={observations} />
    <div className={styles.metrics}>
      <Metric value={counts.total} label="Markers in this report" />
      <Metric value={counts.flagged} label="Flagged by the lab" tone="flagged" />
      <Metric value={counts.normal} label="Marked normal" />
      <Metric value={counts.unflagged} label="No flag supplied" />
    </div>
    <div className={styles.markerHeading}><h3>{region === 'all' ? 'Biomarkers' : region === 'other' ? 'Other markers' : BODY_REGIONS.find(item => item.id === region)?.label}</h3><span>Values as reported by the lab</span></div>
    {region !== 'all' && <button type="button" className={styles.clearRegion} onClick={onClear}>Clear group filter</button>}
    <div className={styles.filters}>
      <label className={styles.search}><Search size={17} aria-hidden="true" /><span className="sr-only">Search biomarkers</span><input type="search" value={search} placeholder="Search biomarkers" onChange={event => { setSearch(event.target.value); setPage(0); }} /></label>
      <label className={styles.check}><input type="checkbox" checked={flaggedOnly} onChange={event => { setFlaggedOnly(event.target.checked); setPage(0); if (event.target.checked) interacted('flagged_only'); }} />Flagged only</label>
    </div>
    <div className={styles.results}>
      {visible.map((observation, index) => <LabObservationRow key={`${observation.code}-${currentPage}-${index}`} observation={observation} />)}
      {!visible.length && <p className={styles.message}>{observations.length ? 'No biomarkers match these filters.' : 'This report has no individual markers. Open the full report to check for a PDF.'}</p>}
    </div>
    {filtered.length > PAGE_SIZE && <nav className={styles.pagination} aria-label="Biomarker pages">
      <button disabled={currentPage === 0} onClick={() => setPage(currentPage - 1)}>Previous</button>
      <span>Page {currentPage + 1} of {pages}</span>
      <button disabled={currentPage === pages - 1} onClick={() => setPage(currentPage + 1)}>Next</button>
    </nav>}
    {!!report.reportNotes.length && <details className={styles.comments}><summary>Lab comments ({report.reportNotes.length})</summary><ul>{report.reportNotes.map((note, i) => <li key={i}>{note}</li>)}</ul></details>}
    {report.orderingProvider?.lastName && <p className={styles.footnote}>Ordered by {[report.orderingProvider.firstName, report.orderingProvider.lastName].filter(Boolean).join(' ')}</p>}
  </>;
}
function Metric({ value, label, tone }: { value: number; label: string; tone?: 'flagged' }) {
  return <div className={styles.metric} data-tone={tone}><strong>{value}</strong><span>{label}</span></div>;
}
function formatReportDate(value?: string) {
  if (!value || Number.isNaN(new Date(value).getTime())) return 'Date unavailable';
  return new Date(value).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
}
